import { useEffect, useState, type JSX } from 'react';
import { Outlet, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { PortalHeader, SignOutMenu, GearIcon, resolveViewerTier, useSettingsDrawer } from '@cca/ui';
import type { Role } from '@cca/corpus-shared';
import { api, ApiError, type SessionInfo } from '../api/client';
import { useInstance } from './InstanceContext';
import { CorpusSettings } from './CorpusSettings';

/** Shared context every authenticated page receives via the Outlet. */
export interface AppContext {
  session: SessionInfo;
}

export function useAppContext(): AppContext {
  return useOutletContext<AppContext>();
}

const TIER_LABEL: Record<Role, string> = {
  commission: 'Commission',
  clergy: 'Clergy',
  public: 'Reader',
};

export function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { instanceName } = useInstance();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const settings = useSettingsDrawer();

  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((data) => {
        if (alive) setSession(data);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 401) {
          const returnTo = location.pathname + location.search;
          navigate(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
        } else {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // Run once on mount; the redirect target reads the path at that time.
  }, []);

  // Heartbeat for the login audit's "time active": re-ping /me every 3 min while
  // a session exists and the tab is visible, so last-seen tracks real presence.
  useEffect(() => {
    if (!session) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void api.me().catch(() => {});
    }, 180_000);
    return () => window.clearInterval(id);
  }, [session]);

  // Print mode (?print=clean | ?print=annotated) — render a chrome-free frame (no
  // header/footer) so the page is exactly the service (clean, or annotated working
  // copy). Used by the browser print dialog (client-side Save as PDF).
  const printParam = new URLSearchParams(location.search).get('print');
  const printing = printParam === 'clean' || printParam === 'annotated';

  if (loading) return <div className="screen-message">Loading…</div>;

  if (error || !session) {
    return (
      <div className="screen-message">
        <div>
          <p style={{ color: 'var(--color-danger)' }}>Could not load your account.</p>
          <p className="muted" style={{ fontSize: 'var(--text-sm)' }}>
            {error ?? 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  // "Sign out all devices" lives here by the Sign out pill (account plumbing,
  // not work — Steve 2026-07-04); the confirm dialog carries the explanation.
  const signOutAll = async () => {
    const message =
      'Sign out all devices?\n\nThis ends every signed-in Liturgy Portal session for your account, on every device, including this one. Other devices are signed out within about a minute. You can sign straight back in.';
    if (!window.confirm(message)) return;
    try {
      await api.logoutAll();
      localStorage.removeItem('codex_email');
      window.location.assign('/');
    } catch {
      window.alert('Could not sign out all devices. Please try again.');
    }
  };

  const viewer = resolveViewerTier({ isRegistrar: session.isAdmin });

  // A fragment: PortalHeader owns the slot layout and its narrow-screen wrap (AUTH-1).
  const rightSlot = (
    <>
      <span className="tier-badge">{TIER_LABEL[session.tier]}</span>
      <button
        type="button"
        className="btn-header"
        aria-label="Settings"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        {...settings.buttonProps}
      >
        <GearIcon />
        Settings
      </button>
      <SignOutMenu
        onSignOut={() => {
          window.location.href = '/auth/logout';
        }}
        onSignOutAll={() => void signOutAll()}
      />
    </>
  );

  if (printing) {
    return (
      <div className="app printing">
        <main className="app-main print-main">
          <Outlet context={{ session } satisfies AppContext} />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <PortalHeader portalName={instanceName} toolName="Corpus" rightSlot={rightSlot} />
      <main className="app-main">
        <Outlet context={{ session } satisfies AppContext} />
      </main>
      <footer className="app-footer">CCAANZ Corpus · {new Date().getFullYear()}</footer>
      <CorpusSettings
        open={settings.open}
        onClose={settings.onClose}
        viewer={viewer}
        session={session}
        onSignOutAll={() => void signOutAll()}
      />
    </div>
  );
}
